import React from 'react';
import { Listing, ListingRow } from 'components/Listing/Listing';
import { LeftRightItem, TopBottomItem } from 'components/Listing/ListingItem';
import {
  PageListingRow,
  PageListingItem,
  PageListingDescriptionItem,
} from 'components/Listing/PageListing';
import Format from 'helpers/format';
import get from 'lodash/get';

import { Button } from '@lightspeed/flame/Button';
import { IconPrinters } from '@lightspeed/flame/Icon/Printers';

export default class PaymentInfoReportTransactionDetails extends React.Component {
  /**
   * Maps ugly gatewayIDs to pretty UI-friendly strings
   *
   * @returns {Array} Array of mappings
   */
  static getGatewayNameMapping() {
    const mapping = [];

    mapping.barclaycard = 'Barclaycard';
    mapping['bnp-paribas'] = 'BNP Paribas';
    mapping.concardis = 'Concardis';
    mapping.izettle = 'iZettle';
    mapping['first-data'] = 'First Data';
    mapping.tyro = 'Tyro';
    mapping['vantiv-us'] = 'Vantiv';
    mapping['vantiv-canada'] = 'Vantiv';
    mapping.worldline = 'Worldline';
    mapping['lightspeed-payments'] = 'Lightspeed';

    return mapping;
  }

  /**
   * The Transaction Details table is built dynamically. Some of the values
   * we try to spit out don't look super great.
   *
   * Make a educated guess as to what kind of value we're spitting out
   * (e.g. Price, Word, ID) and format it accordingly.
   *
   * @param value - The value to format
   */
  static format(value) {
    let ret = value;

    const gatewayNameMapping = PaymentInfoReportTransactionDetails.getGatewayNameMapping();

    if (gatewayNameMapping[value]) {
      // If the value matches one of the Gateway names, spit out its
      // formatted value
      ret = gatewayNameMapping[value];
    } else if (value && !/\d/.test(value)) {
      // If the value contains no numbers, it's probably one or more words.
      // Capitalize it.
      ret = value.charAt(0).toUpperCase() + value.slice(1);
    }

    return ret || 'N/A';
  }

  render() {
    const { paymentTransactionData = {}, paymentData = {}, gateway, accountID } = this.props;
    const cardRecord = paymentTransactionData.CardRecord || {};
    const transactionType = PaymentInfoReportTransactionDetails.format(
      paymentTransactionData.gatewayTransactionType
    );
    const status = PaymentInfoReportTransactionDetails.format(paymentData.status);
    const cardBrand = PaymentInfoReportTransactionDetails.format(cardRecord.cardBrand);
    const entryMethod = PaymentInfoReportTransactionDetails.format(cardRecord.entryMethod);
    const cardHolderName = PaymentInfoReportTransactionDetails.format(cardRecord.cardHolderName);
    const paymentProcessor = PaymentInfoReportTransactionDetails.format(
      paymentTransactionData.gateway
    );
    const processorReference = PaymentInfoReportTransactionDetails.format(
      paymentTransactionData.gatewayRecordRef
    );
    const processorGatewayResponseCode = PaymentInfoReportTransactionDetails.format(
      paymentTransactionData.gatewayResponseCode
    );
    const processorResponseMessage = PaymentInfoReportTransactionDetails.format(
      paymentTransactionData.message
    );
    const maskedPAN = PaymentInfoReportTransactionDetails.format(cardRecord.cardNumber);
    const transactionLSID = PaymentInfoReportTransactionDetails.format(
      paymentTransactionData.paymentTransactionID
    );
    const requestedAmount = Format.formatPrice(paymentTransactionData.requestedAmount);
    const authorizedAmount = Format.formatPrice(paymentTransactionData.authorizedAmount);

    const salePaymentId = get(paymentData, 'SalePayment.salePaymentID');
    const printTransactionReceiptFunc = () => {
      window.merchantos.print.printUrl({
        url:
          `/API/Account/${accountID}/DisplayTemplate/SalePayment/${salePaymentId}` +
          '.html?template=SalePayment&print=1&page_width=auto&page_height=2000mm',
        pageType: 'receipt',
      });
    };

    return (
      <PageListingRow>
        <PageListingDescriptionItem
          mainText="Transaction Details"
          secondaryText="Detailed information of the integrated payment."
        >
          {gateway === 'lightspeed-payments' || gateway === 'vantiv-tripos' ? (
            <Button
              id="print-transaction-receipt"
              onClick={printTransactionReceiptFunc}
              disabled={salePaymentId === null}
            >
              <IconPrinters />
              Print Transaction Receipt
            </Button>
          ) : null}
        </PageListingDescriptionItem>
        <PageListingItem>
          <Listing>
            <ListingRow>
              <TopBottomItem top="Transaction Type" bottom={transactionType} notranslate />
              <TopBottomItem top="Status" bottom={status} notranslate />
            </ListingRow>
            <ListingRow>
              <LeftRightItem left="Card Brand" right={cardBrand} notranslate />
              <LeftRightItem left="Entry Method" right={entryMethod} notranslate />
            </ListingRow>
            <ListingRow>
              <LeftRightItem left="Cardholder Name" right={cardHolderName} notranslate />
              <LeftRightItem left="Payment Processor" right={paymentProcessor} notranslate />
            </ListingRow>
            <ListingRow>
              <LeftRightItem left="Requested Amount" right={requestedAmount} notranslate />
              <LeftRightItem left="Processor Reference" right={processorReference} notranslate />
            </ListingRow>
            <ListingRow>
              <LeftRightItem left="Authorized Amount" right={authorizedAmount} notranslate />
              <TopBottomItem
                top="Processor Response Message"
                bottom={
                  processorResponseMessage !== 'N/A'
                    ? processorResponseMessage
                    : processorGatewayResponseCode
                }
                notranslate
                small
              />
            </ListingRow>
            <ListingRow>
              <TopBottomItem top="Masked PAN" bottom={maskedPAN} notranslate small />
              <TopBottomItem top="Transaction LSID" bottom={transactionLSID} notranslate small />
            </ListingRow>
          </Listing>
        </PageListingItem>
      </PageListingRow>
    );
  }
}
